import { Fragment, useState } from 'react';
import {
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { CurrencyInput } from '@/components/ui/currency-input';
import { FieldError } from '@/components/forms/FieldError';
import {
  NO_FIELD_ERRORS,
  useDeferredValidationErrors,
} from '@/components/forms/useDeferredValidationErrors';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConversionSuggestionsCard } from '@/components/fund-detail/ConversionSuggestionsCard';
import {
  useCreateCurrencyConversion,
  useForeignCashPositions,
} from '@/hooks/react-query/useCurrencyConversions';
import { CurrencyAccountStatement } from './CurrencyAccountStatement';
import { CurrencyTransactionsList } from './CurrencyTransactionsList';
import { useFxCurrencySymbols } from '@/hooks/useFxCurrencySymbols';
import { formatCurrency, formatCurrencyOption } from '@/utils/formatters';
import {
  hasAnyError,
  validateCurrencyConversion,
  type CurrencyConversionErrors,
  type CurrencyConversionFormState,
} from '@/components/fund-detail/currencyConversionValidators';

interface FundCurrencyConversionsProps {
  fundId: string;
  fundCurrency: string;
}

function emptyState(baseCurrency: string): CurrencyConversionFormState {
  return {
    conversionDate: '',
    fromCurrency: baseCurrency.toUpperCase(),
    fromAmount: null,
    toCurrency: '',
    toAmount: null,
  };
}

/**
 * Currency accounts and the form that moves money between them.
 *
 * Both amounts are entered as the bank actually moved them, rather than one
 * being derived from the other at a rate. That is the point of the screen: the
 * gap between what the bank paid and the official rate for the day is the
 * bank's margin, and the ledger keeps the two apart.
 */
interface BalancesPlaceholderProps {
  isLoading: boolean;
  isError: boolean;
}

function BalancesPlaceholder({ isLoading, isError }: BalancesPlaceholderProps) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading balances…</p>;
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        The currency balances could not be loaded. Reload before recording
        anything - a fund whose holdings are unknown is not a fund holding
        nothing.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      No currency accounts yet. Add a currency conversion and one is opened for
      that currency.
    </p>
  );
}

export function FundCurrencyConversions({
  fundId,
  fundCurrency,
}: FundCurrencyConversionsProps) {
  const [state, setState] = useState<CurrencyConversionFormState>(() =>
    emptyState(fundCurrency)
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  // One statement at a time: they are long enough that two open at once buries
  // the balances the table exists to show.
  const [openCurrency, setOpenCurrency] = useState<string | null>(null);

  const positions = useForeignCashPositions(fundId);
  const { currencies, getSymbol } = useFxCurrencySymbols();
  const createConversion = useCreateCurrencyConversion(fundId);

  const errors: CurrencyConversionErrors = validateCurrencyConversion(state);
  const { visibleErrors, revealErrors, hideErrors } =
    useDeferredValidationErrors<CurrencyConversionErrors>(
      errors,
      NO_FIELD_ERRORS
    );

  const update = <K extends keyof CurrencyConversionFormState>(
    field: K,
    value: CurrencyConversionFormState[K]
  ) => setState((previous) => ({ ...previous, [field]: value }));

  // Closing throws the draft away, so reopening never starts half filled in.
  const closeForm = () => {
    setIsFormOpen(false);
    setState(emptyState(fundCurrency));
    hideErrors();
  };

  const handleSubmit = async () => {
    if (hasAnyError(errors)) {
      revealErrors();
      return;
    }
    await createConversion.mutateAsync({
      conversion_date: state.conversionDate,
      from_currency: state.fromCurrency.toUpperCase(),
      from_amount: String(state.fromAmount),
      to_currency: state.toCurrency.toUpperCase(),
      to_amount: String(state.toAmount),
    });
    closeForm();
  };

  // The whole response, not just its rows: the balances and the date they were
  // measured at travel together, and the statement needs both.
  const holdings = positions.data;

  return (
    <div className="space-y-4">
      <ConversionSuggestionsCard fundId={fundId} />
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start gap-4">
            <div>
              <CardTitle>Currency accounts</CardTitle>
              <CardDescription>
                What this fund holds in each currency, and what those holdings
                are currently carried at in {fundCurrency.toUpperCase()}.
              </CardDescription>
            </div>
            {isFormOpen ? (
              <Button variant="outline" onClick={closeForm}>
                Cancel
              </Button>
            ) : (
              <Button onClick={() => setIsFormOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add currency conversion
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {positions.isLoading ||
          positions.isError ||
          !holdings ||
          holdings.positions.length === 0 ? (
            <BalancesPlaceholder
              isLoading={positions.isLoading}
              isError={positions.isError}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Held</TableHead>
                  <TableHead className="text-right">
                    Carried at ({fundCurrency.toUpperCase()})
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holdings.positions.map((position) => {
                  const isOpen = openCurrency === position.currency;
                  return (
                    <Fragment key={position.account_code}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          setOpenCurrency(isOpen ? null : position.currency)
                        }
                      >
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-1">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            {position.currency}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(position.foreign_balance, {
                            currency: position.currency,
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(position.functional_balance, {
                            currency: fundCurrency,
                          })}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={3} className="px-4 py-2">
                            <CurrencyAccountStatement
                              fundId={fundId}
                              accountCode={position.account_code}
                              currency={position.currency}
                              fundCurrency={fundCurrency}
                              closingBalance={position.foreign_balance}
                              closingValue={position.functional_balance}
                              asOf={holdings.as_of}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {isFormOpen && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" />
              Record a conversion
            </CardTitle>
            <CardDescription>
              Enter both amounts as the bank actually moved them. The conversion
              is saved as a draft - attach its bank entry and approve it to post
              to the ledger.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1 max-w-xs">
              <Label htmlFor="conversion-date">Conversion date</Label>
              <Input
                id="conversion-date"
                type="date"
                value={state.conversionDate}
                onChange={(e) => update('conversionDate', e.target.value)}
              />
              <FieldError message={visibleErrors.conversionDate} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="conversion-from-currency">Converted from</Label>
                <Select
                  value={state.fromCurrency}
                  onValueChange={(value) => update('fromCurrency', value)}
                >
                  <SelectTrigger id="conversion-from-currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.id} value={currency.iso_code}>
                        {formatCurrencyOption(currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={visibleErrors.fromCurrency} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="conversion-from-amount">Amount paid</Label>
                <CurrencyInput
                  id="conversion-from-amount"
                  value={state.fromAmount}
                  onChange={(value) => update('fromAmount', value)}
                  currency={state.fromCurrency}
                  symbol={getSymbol(state.fromCurrency)}
                  decimalPlaces={2}
                  placeholder="0.00"
                />
                <FieldError message={visibleErrors.fromAmount} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="conversion-to-currency">Converted to</Label>
                <Select
                  value={state.toCurrency}
                  onValueChange={(value) => update('toCurrency', value)}
                >
                  <SelectTrigger id="conversion-to-currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.id} value={currency.iso_code}>
                        {formatCurrencyOption(currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={visibleErrors.toCurrency} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="conversion-to-amount">Amount received</Label>
                <CurrencyInput
                  id="conversion-to-amount"
                  value={state.toAmount}
                  onChange={(value) => update('toAmount', value)}
                  currency={state.toCurrency}
                  symbol={getSymbol(state.toCurrency)}
                  decimalPlaces={2}
                  placeholder="0.00"
                />
                <FieldError message={visibleErrors.toAmount} />
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                onClick={handleSubmit}
                disabled={createConversion.isPending}
              >
                {createConversion.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Record conversion
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <CurrencyTransactionsList fundId={fundId} />
    </div>
  );
}
